import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { pwd503Api, ApprovalCandidate } from '../../services/pwd503Api';
import { BatchAcceptanceBridge, pwd503f1AcceptanceManifest } from '../../features/acceptance';
import './PWD503Form.css';

// ---- Item state with selection ----
interface ItemState {
  item: ApprovalCandidate;
  selected: boolean;
}

// ---- Component ----
const PWD503Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await pwd503Api.queryCandidates(startNo, endNo);
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的託工單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fd130_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute approval ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要核准的託工單');
      return;
    }
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆託工單？`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.fd130_01);
        setExecuting(true);
        try {
          const res = await pwd503Api.approve(selectedNos);
          setLastApiResponse(res);
          if (res.failed === 0) {
            message.success(res.message || `核准完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '核准執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  // ---- Status label helper ----
  const statusLabel = (code: string): string => {
    if (!code || code.trim() === '') return '未核准';
    if (code === 'P') return '已完成';
    return `部分核准 (${code})`;
  };

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fd130_01, e.target.checked)}
        />
      ),
    },
    {
      title: '託工單號',
      dataIndex: ['item', 'fd130_01'],
      key: 'fd130_01',
      width: 110,
    },
    {
      title: '託工日期',
      dataIndex: ['item', 'fd130_11'],
      key: 'fd130_11',
      width: 110,
    },
    {
      title: '加工廠',
      dataIndex: ['item', 'fd130_12'],
      key: 'fd130_12',
      width: 150,
    },
    {
      title: '預定交期',
      dataIndex: ['item', 'fd130_13'],
      key: 'fd130_13',
      width: 110,
    },
    {
      title: '核准狀態',
      dataIndex: ['item', 'fd130_80'],
      key: 'fd130_80',
      width: 120,
      render: (_: unknown, record: ItemState) =>
        statusLabel(record.item.fd130_80),
    },
  ];

  // ---- Render ----
  return (
    <div className="pwd503-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                生產管理
              </a>
              <span>/</span>
              <span className="current">計劃/受訂託工單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>計劃/受訂託工單核准</h1>
            <span className="badge">PWD503</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="fd130_01">託工單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWD503-ACT-LIST-CANDIDATES">
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fd130_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入託工單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
            data-acceptance-action="PWD503-ACT-APPROVE"
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwd503f1AcceptanceManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fd130_01)}
          keyOf={(item) => item.fd130_01}
          queryExact={async (key) => (await pwd503Api.queryCandidates(key, key)).items?.[0] || null}
          lastApiResponse={lastApiResponse}
          loading={loading || executing}
          onAutoFill={() => {
            const first = items[0];
            if (first) setItems(prev => prev.map((state, index) => ({ ...state, selected: index === 0 })));
          }}
          autoFillDisabled={items.length === 0}
          autoFillReason="只選取正式 API 回傳的第一筆託工單候選，核准仍由原按鈕執行。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default PWD503Form;
